import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DateField } from '../dropdown';
import type {
  PartOrder,
  PartOrderFulfillmentMethod,
} from '@techgitter/contracts';
import { fetchPartOrder, updatePartOrder } from '../../services/partOrdersApi';

interface Props {
  open: boolean;
  partOrderId: string | null;
  onClose: (updated: boolean) => void;
}

/**
 * Edit a placed Part Order header. Opens with a fresh fetch of the header
 * (so we don't show stale values cached from the lines list) and PATCHes
 * back via the existing update endpoint. Line edits (qty, sku, actual
 * cost) are out of scope here — those go through cancel + recreate.
 */
export const EditPartOrderDialog: React.FC<Props> = ({ open, partOrderId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [po, setPo] = useState<PartOrder | null>(null);

  const [poNumber, setPoNumber] = useState('');
  const [vendorOrderNumber, setVendorOrderNumber] = useState('');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [fulfillmentMethod, setFulfillmentMethod] =
    useState<PartOrderFulfillmentMethod>('delivery');
  const isPickup = fulfillmentMethod === 'pickup';
  const [notes, setNotes] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !partOrderId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchPartOrder(partOrderId)
      .then((data) => {
        if (cancelled) return;
        setPo(data);
        setPoNumber(data.poNumber || '');
        setVendorOrderNumber(data.vendorOrderNumber || '');
        setExpectedDeliveryDate(data.expectedDeliveryDate || '');
        setFulfillmentMethod(data.fulfillmentMethod || 'delivery');
        setNotes(data.notes || '');
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, partOrderId]);

  const reset = () => {
    setPo(null);
    setPoNumber('');
    setVendorOrderNumber('');
    setExpectedDeliveryDate('');
    setFulfillmentMethod('delivery');
    setNotes('');
    setLoadError(null);
    setSaveError(null);
  };

  const handleClose = (updated: boolean) => {
    reset();
    onClose(updated);
  };

  const canSave = !!po && !saving && poNumber.trim().length > 0;

  const handleSave = async () => {
    if (!po) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updatePartOrder(po.id, {
        po_number: poNumber.trim(),
        vendor_order_number: vendorOrderNumber.trim() || null,
        expected_delivery_date: expectedDeliveryDate || null,
        fulfillment_method: fulfillmentMethod,
        // Pickup carries no freight — zero shipping when switching to pickup.
        ...(isPickup ? { shipping_cost_ex_gst: 0 } : {}),
        notes: notes.trim() || null,
      });
      handleClose(true);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => handleClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit part order{po?.vendorName ? ` — ${po.vendorName}` : ''}
      </DialogTitle>
      <DialogContent>
        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}
        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : po ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <TextField
                label="PO Number"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                required
                inputProps={{ size: 8 }}
                sx={{ width: 'calc(8ch + 56px)' }}
              />
              <TextField
                label="Vendor Order #"
                value={vendorOrderNumber}
                onChange={(e) => setVendorOrderNumber(e.target.value)}
                inputProps={{ size: 12 }}
                sx={{ width: 'calc(12ch + 56px)' }}
              />
              <div style={{ width: 200 }}>
                <DateField
                  label={isPickup ? 'Ready for collection' : 'Expected Delivery'}
                  value={expectedDeliveryDate || null}
                  onChange={(iso) => setExpectedDeliveryDate(iso ?? '')}
                />
              </div>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Fulfilment
              </Typography>
              <ToggleButtonGroup
                value={fulfillmentMethod}
                exclusive
                onChange={(_, next: PartOrderFulfillmentMethod | null) => {
                  if (next) setFulfillmentMethod(next);
                }}
                color="primary"
                size="small"
              >
                <ToggleButton value="delivery">Delivery</ToggleButton>
                <ToggleButton value="pickup">Pickup</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <TextField
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose(false)} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={!canSave}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
